import { useEffect, useState } from "react";
import { b2bTierForSpend, orderSpendExVatCents } from "@glastotaal/core";
import type { Order } from "@/types";
import { useCustomerAuth } from "./auth";
import { useProfile } from "./useProfile";
import { getMyDiscount, type CustomerDiscount } from "./profile";
import { getMyOrders } from "./orders";

/** Betaalde jaaromzet in het lopende kalenderjaar = het HELE orderbedrag (incl. btw + verzending),
 *  eerlijker/duidelijker voor de klant. Moet identiek blijven aan de server
 *  (`businessYearSpendCents` in createOrder.ts) én aan AccountHome. */
const PAID = new Set(["paid", "te_beoordelen", "approved", "ordered_at_supplier", "confirmed_by_supplier", "shipped", "completed"]);
export function yearSpendCents(orders: Order[]): number {
  const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
  // Excl. btw — de staffeldrempels zijn zo bedoeld (zie B2B_TIERS). Zelfde helper als de server,
  // zodat de voortgang die de klant ziet niet afwijkt van de korting die straks wordt toegepast.
  return orders.reduce(
    (sum, o) => (PAID.has(o.status) && (o.createdAt ?? 0) >= yearStart ? sum + orderSpendExVatCents(o.totals) : sum),
    0,
  );
}

/**
 * De korting die de server bij het afrekenen ÉCHT toepast — zodat winkelwagen/checkout dezelfde
 * korting tonen als er straks berekend wordt. Spiegelt `createOrder.ts`:
 *  1) handmatige per-klant korting (customerDiscounts/{uid}) heeft voorrang;
 *  2) anders, voor een niet-ingetrokken zakelijk account: de staffelkorting op de betaalde jaaromzet.
 * Gast/particulier zonder korting → null.
 */
export function useEffectiveDiscount(): CustomerDiscount | null {
  const { signedIn, user } = useCustomerAuth();
  const { profile } = useProfile();
  const [discount, setDiscount] = useState<CustomerDiscount | null>(null);
  const isBusiness = !!profile?.isBusiness && profile.businessStatus !== "revoked";
  useEffect(() => {
    if (!signedIn || !user?.uid) { setDiscount(null); return; }
    let alive = true;
    const uid = user.uid;
    (async () => {
      const manual = await getMyDiscount(uid).catch(() => null);
      if (!alive) return;
      if (manual) { setDiscount(manual); return; }
      if (!isBusiness) { setDiscount(null); return; }
      const orders = await getMyOrders(uid).catch(() => [] as Order[]);
      if (!alive) return;
      const tier = b2bTierForSpend(yearSpendCents(orders));
      setDiscount(tier.percent > 0 ? { kind: "percent", value: tier.percent, note: "Zakelijke staffelkorting (jaaromzet)" } : null);
    })();
    return () => { alive = false; };
  }, [signedIn, user?.uid, isBusiness]);
  return discount;
}
